/**
 * Product classification for the 36 native tools: what each call does, the
 * strictest posture launch allows, and the MCP grant it needs.
 *
 * The launch posture column of the design (section 6.2) is encoded here once.
 * Descriptions never carry policy; policy is evaluated at runtime from this
 * table so a settings change cannot leave stale permission language in a
 * prompt.
 */
import type { SpecToolDecision } from '@parcel/types';
import { MANAGE_KNOWLEDGE_COMMANDS } from './native-descriptors.js';
import type {
  NativeToolCategory,
  NativeToolEffect,
  NativeToolIdempotency,
  NativeToolPreview,
  NativeToolSurface,
  ParcelMcpScope,
} from './types.js';

/** Trusted approval-card builders, implemented by the executor in PR 08/09. */
export const PREVIEWER_KEYS = [
  'workspace_record_upsert',
  'workspace_record_delete',
  'workspace_subscription_upsert',
  'workspace_subscription_delete',
  'workspace_view_manage',
  'workspace_follow',
  'workspace_member_change',
  'workspace_invitation_change',
  'data_feedback_submit',
  'enrich_spend',
  'knowledge_manage',
  'workspace_scores_manage',
] as const;

/** Audit redactors: each names the allowlist applied before an audit write. */
export const REDACTOR_KEYS = [
  'read_result_summary',
  'workspace_record_change',
  'workspace_subscription_change',
  'workspace_view_change',
  'workspace_member_change',
  'workspace_invitation_change',
  'data_feedback_report',
  'enrich_result',
  'knowledge_change',
] as const;

export type PreviewerKey = (typeof PREVIEWER_KEYS)[number];
export type RedactorKey = (typeof REDACTOR_KEYS)[number];

export interface NativeToolMetadata {
  category: NativeToolCategory;
  effect: NativeToolEffect;
  defaultPolicy: SpecToolDecision;
  requiredScope: ParcelMcpScope;
  requiredPlan?: 'pro';
  requiredRole: 'member' | 'admin' | 'owner';
  registryFloor: SpecToolDecision;
  idempotency: NativeToolIdempotency;
  previewer: PreviewerKey | null;
  redactor: RedactorKey;
  preview: NativeToolPreview | null;
  resolveEffect?: (args: Readonly<Record<string, unknown>>) => NativeToolEffect;
  /** Absent means both surfaces. */
  surfaces?: readonly NativeToolSurface[];
}

// Every tool on this surface is gated on the Pro plan today: /data/* returns 403
// when the workspace credit ceiling is 0, and every /workspace/* route calls
// requireProWorkspace. This is the Developer MCP entitlement; the trusted Spec
// surface (PR 06) bypasses programmatic-plan metering, never authorization.
const PRO = 'pro' as const;

function read(category: NativeToolCategory, requiredScope: ParcelMcpScope): NativeToolMetadata {
  return {
    category,
    effect: 'read',
    defaultPolicy: 'always_allow',
    requiredScope,
    requiredPlan: PRO,
    requiredRole: 'member',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
    previewer: null,
    redactor: 'read_result_summary',
    preview: null,
  };
}

/** query_data's route meters every plan by its own daily allowance, so no plan gate applies. */
function everyPlan(metadata: NativeToolMetadata): NativeToolMetadata {
  const { requiredPlan: _pro, ...rest } = metadata;
  return rest;
}

/** Reversible writes: approvable at launch, never automatic. */
function reversibleWrite(opts: {
  category: NativeToolCategory;
  previewer: PreviewerKey;
  redactor: RedactorKey;
  preview: NativeToolPreview;
  idempotency?: NativeToolIdempotency;
  requiredScope?: ParcelMcpScope;
  requiredRole?: 'member' | 'admin' | 'owner';
  resolveEffect?: (args: Readonly<Record<string, unknown>>) => NativeToolEffect;
}): NativeToolMetadata {
  return {
    category: opts.category,
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    requiredScope: opts.requiredScope ?? 'workspace:write',
    requiredPlan: PRO,
    requiredRole: opts.requiredRole ?? 'member',
    registryFloor: 'needs_approval',
    idempotency: opts.idempotency ?? 'keyed',
    previewer: opts.previewer,
    redactor: opts.redactor,
    preview: opts.preview,
    ...(opts.resolveEffect ? { resolveEffect: opts.resolveEffect } : {}),
  };
}

/**
 * Destructive and admin/security operations. Blocked at launch: they stay
 * documented, visible in Settings, and audited, but no workspace setting can
 * make them executable until a later release lowers the floor.
 */
function blockedOperation(opts: {
  category: NativeToolCategory;
  effect: 'destructive' | 'admin_security';
  previewer: PreviewerKey;
  redactor: RedactorKey;
  preview: NativeToolPreview;
  idempotency: NativeToolIdempotency;
  requiredScope: ParcelMcpScope;
  requiredRole: 'member' | 'admin' | 'owner';
}): NativeToolMetadata {
  return {
    category: opts.category,
    effect: opts.effect,
    defaultPolicy: 'blocked',
    requiredScope: opts.requiredScope,
    requiredPlan: PRO,
    requiredRole: opts.requiredRole,
    registryFloor: 'blocked',
    idempotency: opts.idempotency,
    previewer: opts.previewer,
    redactor: opts.redactor,
    preview: opts.preview,
  };
}

/**
 * workspace_manage_views multiplexes eight operations behind one name, so a
 * workspace-wide "Needs approval" choice must not make the destructive one
 * executable. Anything but a recognized non-delete action resolves destructive:
 * an unparsed or novel action is treated as the worst case, not the best.
 *
 * Exported because it is a policy surface, not an implementation detail: every
 * member of this set is an operation Spec may run once a member approves it, so
 * the registry gate asserts the set itself. Adding an eighth has to fail a test
 * before it can ship.
 */
export const NON_DESTRUCTIVE_VIEW_ACTIONS: ReadonlySet<string> = new Set([
  'create',
  'update',
  'add_records',
  'remove_records',
  'favorite',
  'unfavorite',
  'watch',
]);

function resolveManageViewsEffect(args: Readonly<Record<string, unknown>>): NativeToolEffect {
  const action = args.action;
  if (typeof action !== 'string') return 'destructive';
  return NON_DESTRUCTIVE_VIEW_ACTIONS.has(action) ? 'reversible_write' : 'destructive';
}

const KNOWLEDGE_COMMANDS: ReadonlySet<string> = new Set(MANAGE_KNOWLEDGE_COMMANDS);

/** Every command is reversible (delete archives); anything unknown, or no command, is treated as destructive. */
export function resolveManageKnowledgeEffect(
  args: Readonly<Record<string, unknown>>,
): NativeToolEffect {
  const commands = Array.isArray(args.inputs)
    ? (args.inputs as unknown[]).map((item) => (item as Record<string, unknown> | null)?.command)
    : [args.command];
  return commands.length > 0 &&
    commands.every((command) => typeof command === 'string' && KNOWLEDGE_COMMANDS.has(command))
    ? 'reversible_write'
    : 'destructive';
}

const recordUpsertPreview = (
  entity: string,
  identifiers: readonly string[],
): NativeToolPreview => ({
  title: `Update ${entity} in your workspace book`,
  subjectFields: [...identifiers, 'inputs'],
  sensitiveFields: ['notes'],
});

const recordDeletePreview = (entity: string): NativeToolPreview => ({
  title: `Remove ${entity} from your workspace book`,
  subjectFields: ['id', 'ids'],
  sensitiveFields: [],
});

export const NATIVE_TOOL_METADATA: Readonly<Record<string, NativeToolMetadata>> = {
  // Retired from Spec by Spec-Tools PR5; query_data covers them. MCP keeps them.
  // Global data reads
  search_accounts: { ...read('data', 'data:read'), surfaces: ['mcp'] },
  get_accounts: { ...read('data', 'data:read'), surfaces: ['mcp'] },
  search_contacts: { ...read('data', 'data:read'), surfaces: ['mcp'] },
  get_contacts: { ...read('data', 'data:read'), surfaces: ['mcp'] },
  search_projects: { ...read('data', 'data:read'), surfaces: ['mcp'] },
  get_projects: { ...read('data', 'data:read'), surfaces: ['mcp'] },
  search_signals: { ...read('data', 'data:read'), surfaces: ['mcp'] },
  get_signals: { ...read('data', 'data:read'), surfaces: ['mcp'] },

  // Field discovery
  search_fields: { ...read('fields', 'data:read'), surfaces: ['mcp'] },
  get_fields: read('fields', 'data:read'),

  // Workspace book reads; Spec keeps the searches for reading a saved View.
  workspace_search_accounts: read('workspace', 'workspace:read'),
  workspace_get_account: { ...read('workspace', 'workspace:read'), surfaces: ['mcp'] },
  workspace_search_contacts: read('workspace', 'workspace:read'),
  workspace_get_contact: { ...read('workspace', 'workspace:read'), surfaces: ['mcp'] },
  workspace_search_projects: read('workspace', 'workspace:read'),
  workspace_get_project: { ...read('workspace', 'workspace:read'), surfaces: ['mcp'] },

  // Views
  workspace_search_views: read('views', 'workspace:read'),
  workspace_manage_views: reversibleWrite({
    category: 'views',
    previewer: 'workspace_view_manage',
    redactor: 'workspace_view_change',
    preview: {
      title: 'Change a shared workspace View',
      subjectFields: ['action', 'id', 'ids', 'view_id', 'view_ids', 'name', 'dataset', 'inputs'],
      sensitiveFields: [],
    },
    resolveEffect: resolveManageViewsEffect,
  }),
  workspace_feed: read('views', 'workspace:read'),
  workspace_follow: reversibleWrite({
    category: 'views',
    previewer: 'workspace_follow',
    redactor: 'workspace_view_change',
    preview: {
      title: 'Follow, unfollow or skip accounts and projects',
      subjectFields: ['action', 'dataset', 'record_id', 'record_ids'],
      sensitiveFields: [],
    },
  }),

  // Scoring
  workspace_scores: read('views', 'workspace:read'),
  workspace_manage_scores: reversibleWrite({
    category: 'views',
    previewer: 'workspace_scores_manage',
    redactor: 'workspace_view_change',
    requiredRole: 'admin',
    preview: {
      title: 'Change workspace scoring',
      subjectFields: ['action', 'key'],
      sensitiveFields: [],
    },
  }),

  // Knowledge (Spec has it on every plan; the Pro gate is MCP's and the API's)
  find_knowledge: read('knowledge', 'knowledge:read'),
  get_knowledge: read('knowledge', 'knowledge:read'),
  manage_knowledge: reversibleWrite({
    category: 'knowledge',
    previewer: 'knowledge_manage',
    redactor: 'knowledge_change',
    requiredScope: 'knowledge:write',
    preview: {
      title: 'Change workspace knowledge',
      subjectFields: ['command', 'page', 'path', 'title', 'inputs'],
      sensitiveFields: [],
    },
    resolveEffect: resolveManageKnowledgeEffect,
  }),

  // Record writes
  workspace_upsert_account: reversibleWrite({
    category: 'workspace',
    previewer: 'workspace_record_upsert',
    redactor: 'workspace_record_change',
    preview: recordUpsertPreview('an account', ['account_id', 'match']),
  }),
  workspace_delete_account: blockedOperation({
    category: 'workspace',
    effect: 'destructive',
    previewer: 'workspace_record_delete',
    redactor: 'workspace_record_change',
    preview: recordDeletePreview('an account'),
    // The route is idempotent: deleting an absent overlay row still succeeds.
    idempotency: 'safe_retry',
    requiredScope: 'workspace:write',
    requiredRole: 'member',
  }),
  workspace_upsert_project: reversibleWrite({
    category: 'workspace',
    previewer: 'workspace_record_upsert',
    redactor: 'workspace_record_change',
    preview: recordUpsertPreview('a project', ['project_id']),
  }),
  workspace_delete_project: blockedOperation({
    category: 'workspace',
    effect: 'destructive',
    previewer: 'workspace_record_delete',
    redactor: 'workspace_record_change',
    preview: recordDeletePreview('a project'),
    idempotency: 'safe_retry',
    requiredScope: 'workspace:write',
    requiredRole: 'member',
  }),
  workspace_upsert_contact: reversibleWrite({
    category: 'workspace',
    previewer: 'workspace_record_upsert',
    redactor: 'workspace_record_change',
    preview: recordUpsertPreview('a contact', ['contact_id', 'match']),
  }),
  workspace_delete_contact: blockedOperation({
    category: 'workspace',
    effect: 'destructive',
    previewer: 'workspace_record_delete',
    redactor: 'workspace_record_change',
    preview: recordDeletePreview('a contact'),
    idempotency: 'safe_retry',
    requiredScope: 'workspace:write',
    requiredRole: 'member',
  }),

  // Signal subscriptions
  workspace_list_subscriptions: read('subscriptions', 'workspace:read'),
  workspace_upsert_subscription: reversibleWrite({
    category: 'subscriptions',
    previewer: 'workspace_subscription_upsert',
    redactor: 'workspace_subscription_change',
    preview: {
      title: 'Change a signal subscription',
      subjectFields: ['signal_type', 'priority'],
      sensitiveFields: [],
    },
  }),
  workspace_delete_subscription: blockedOperation({
    category: 'subscriptions',
    effect: 'destructive',
    previewer: 'workspace_subscription_delete',
    redactor: 'workspace_subscription_change',
    preview: {
      title: 'Remove a signal subscription',
      subjectFields: ['id'],
      sensitiveFields: [],
    },
    idempotency: 'safe_retry',
    requiredScope: 'workspace:write',
    requiredRole: 'member',
  }),

  // Members
  workspace_list_members: read('members', 'workspace:read'),
  workspace_update_member: blockedOperation({
    category: 'members',
    effect: 'admin_security',
    previewer: 'workspace_member_change',
    redactor: 'workspace_member_change',
    preview: {
      title: 'Change a workspace member role',
      subjectFields: ['userId', 'role'],
      sensitiveFields: [],
    },
    idempotency: 'safe_retry',
    requiredScope: 'workspace:admin',
    requiredRole: 'admin',
  }),
  workspace_delete_member: blockedOperation({
    category: 'members',
    effect: 'admin_security',
    previewer: 'workspace_member_change',
    redactor: 'workspace_member_change',
    preview: {
      title: 'Remove a workspace member',
      subjectFields: ['userId'],
      sensitiveFields: [],
    },
    idempotency: 'safe_retry',
    requiredScope: 'workspace:admin',
    requiredRole: 'admin',
  }),

  // Invitations
  workspace_create_invitation: blockedOperation({
    category: 'members',
    effect: 'admin_security',
    previewer: 'workspace_invitation_change',
    redactor: 'workspace_invitation_change',
    preview: {
      title: 'Invite someone to the workspace',
      subjectFields: ['email', 'role'],
      sensitiveFields: ['email'],
    },
    // A live pending invite makes a repeat 409, but a retry after an unknown
    // outcome can still email a person twice.
    idempotency: 'never_retry_unknown',
    requiredScope: 'workspace:admin',
    requiredRole: 'admin',
  }),
  workspace_list_invitations: read('members', 'workspace:read'),
  workspace_delete_invitation: blockedOperation({
    category: 'members',
    effect: 'admin_security',
    previewer: 'workspace_invitation_change',
    redactor: 'workspace_invitation_change',
    preview: {
      title: 'Revoke a workspace invitation',
      subjectFields: ['id'],
      sensitiveFields: [],
    },
    idempotency: 'safe_retry',
    requiredScope: 'workspace:admin',
    requiredRole: 'admin',
  }),

  // Workspace metadata
  workspace_get_info: read('workspace', 'workspace:read'),
  workspace_get_credits: read('workspace', 'workspace:read'),

  // Data feedback
  submit_data_feedback: reversibleWrite({
    category: 'feedback',
    previewer: 'data_feedback_submit',
    redactor: 'data_feedback_report',
    preview: {
      title: 'Report a data issue to Parcel',
      subjectFields: ['target_type', 'target_id'],
      sensitiveFields: ['body'],
    },
    // The report route has no natural key, so a blind retry files a duplicate.
    idempotency: 'never_retry_unknown',
  }),

  // SQL over spec_read (PR1). Never on MCP: the view schema is not a public contract.
  query_data: { ...everyPlan(read('data', 'data:read')), surfaces: ['spec'] },

  // Parallel via the AI Gateway; every plan, each call charged to the member's credits.
  web_search: { ...everyPlan(read('web', 'data:read')), surfaces: ['spec'] },
  // Enrichment (Enrich PR5, PR6): MCP and Spec. A repeated reveal is free; a repeated search or company page is charged again.
  search_people: {
    ...read('enrich', 'workspace:write'),
    idempotency: 'never_retry_unknown',
    surfaces: ['spec', 'mcp'],
  },
  search_companies: {
    ...read('enrich', 'workspace:write'),
    idempotency: 'never_retry_unknown',
    surfaces: ['spec', 'mcp'],
  },
  reveal_contact: {
    ...reversibleWrite({
      category: 'enrich',
      previewer: 'enrich_spend',
      redactor: 'enrich_result',
      preview: {
        title: 'Spend enrichment credits to reveal contact details',
        subjectFields: ['id', 'ids', 'kinds'],
        sensitiveFields: [],
      },
      idempotency: 'safe_retry',
    }),
    surfaces: ['spec', 'mcp'],
  },
  enrich_from_linkedin: {
    ...reversibleWrite({
      category: 'enrich',
      previewer: 'enrich_spend',
      redactor: 'enrich_result',
      preview: {
        title: 'Spend enrichment credits on LinkedIn profiles',
        subjectFields: ['url', 'urls'],
        sensitiveFields: [],
      },
      idempotency: 'never_retry_unknown',
    }),
    surfaces: ['spec', 'mcp'],
  },
};
